import { getCurrentTemplateMetrics, type Metrics } from "../templates/custom/helpers"

export interface DuplicateViewFlowActions {
  setView: (viewId: string) => void
  setShowMetricsSelector: (show: boolean, options?: { restoreSourceView?: boolean }) => void
  setDuplicateFromViewName: (name: string) => void
  setDuplicateFromTemplateId: (templateId: string | null) => void
  setDuplicateFromViewMetrics: (metrics: string[] | null) => void
  setDuplicateReturnViewId: (viewId: string) => void
  clearDuplicateState: () => void
}

type DuplicateFlowSetters = Pick<DuplicateViewFlowActions, "clearDuplicateState" | "setShowMetricsSelector">

interface BeginEditCustomTemplateParams extends DuplicateFlowSetters {
  templateId: string
  setView: DuplicateViewFlowActions["setView"]
}

interface BeginDuplicateCustomTemplateParams {
  viewName: string
  templateId: string
  sourceViewId: string
  duplicateFlow: DuplicateViewFlowActions
}

interface BeginDuplicateBuiltInViewParams {
  viewName: string
  sourceViewId: string
  sourcedMetricIds: string[] | null
  duplicateFlow: DuplicateViewFlowActions
}

interface GetInitialDuplicateSelectedMetricsParams {
  duplicateFromTemplateId?: string | null
  duplicateFromViewMetrics?: string[] | null
  duplicateSourceMetrics?: Metrics | null
  modelId?: string | null
}

export function beginNewCustomView({ clearDuplicateState, setView, setShowMetricsSelector }: DuplicateFlowSetters & { setView: DuplicateViewFlowActions["setView"] }) {
  clearDuplicateState()
  setView("custom")
  setShowMetricsSelector(true)
}

export function resetDuplicateFlowOnViewSelect({ clearDuplicateState, setShowMetricsSelector }: DuplicateFlowSetters) {
  clearDuplicateState()
  setShowMetricsSelector(false)
}

export function beginEditCustomTemplate({
  templateId,
  clearDuplicateState,
  setView,
  setShowMetricsSelector,
}: BeginEditCustomTemplateParams) {
  clearDuplicateState()
  setView(templateId)
  setShowMetricsSelector(true)
}

interface BeginDuplicateToCustomViewParams {
  viewName: string
  sourceViewId: string
  duplicateFromTemplateId: string | null
  duplicateFromViewMetrics: string[] | null
  duplicateFlow: DuplicateViewFlowActions
}

function beginDuplicateToCustomView({
  viewName,
  sourceViewId,
  duplicateFromTemplateId,
  duplicateFromViewMetrics,
  duplicateFlow,
}: BeginDuplicateToCustomViewParams) {
  const {
    setDuplicateFromViewName,
    setDuplicateFromTemplateId,
    setDuplicateFromViewMetrics,
    setDuplicateReturnViewId,
    setView,
    setShowMetricsSelector,
  } = duplicateFlow

  setDuplicateFromViewName(viewName)
  setDuplicateFromTemplateId(duplicateFromTemplateId)
  setDuplicateFromViewMetrics(duplicateFromViewMetrics)
  setDuplicateReturnViewId(sourceViewId)
  setView("custom")
  setShowMetricsSelector(true)
}

export function beginDuplicateCustomTemplate({
  viewName,
  templateId,
  sourceViewId,
  duplicateFlow,
}: BeginDuplicateCustomTemplateParams) {
  beginDuplicateToCustomView({
    viewName,
    sourceViewId,
    duplicateFromTemplateId: templateId,
    duplicateFromViewMetrics: null,
    duplicateFlow,
  })
}

export function beginDuplicateBuiltInView({
  viewName,
  sourceViewId,
  sourcedMetricIds,
  duplicateFlow,
}: BeginDuplicateBuiltInViewParams) {
  beginDuplicateToCustomView({
    viewName,
    sourceViewId,
    duplicateFromTemplateId: null,
    duplicateFromViewMetrics: sourcedMetricIds,
    duplicateFlow,
  })
}

export function getInitialDuplicateSelectedMetrics({
  duplicateFromTemplateId,
  duplicateFromViewMetrics,
  duplicateSourceMetrics,
  modelId,
}: GetInitialDuplicateSelectedMetricsParams) {
  if (duplicateFromTemplateId) {
    if (!duplicateSourceMetrics || !modelId) {
      return null
    }

    return getCurrentTemplateMetrics(duplicateSourceMetrics, modelId)
  }

  return duplicateFromViewMetrics ?? null
}
